<script setup lang="ts">
import { useAuthStore } from '@/stores/auth'
import axios, { type AxiosResponse } from 'axios'
import { onMounted } from 'vue'
import { useRouter } from 'vue-router'

const { login } = useAuthStore()
const router = useRouter()

type KakaoTokenResponse = {
  access_token: string
  token_type: string
  id_token: string
  refresh_token: string
  expires_in: number
  scope: string
  refresh_token_expires_in: number
}

const getKakaoToken = async (
  code: string,
): Promise<AxiosResponse<KakaoTokenResponse> | unknown> => {
  try {
    const data = {
      grant_type: 'authorization_code',
      client_id: import.meta.env.VITE_KAKAO_REST_API_KEY, // REST API 키
      redirect_uri: import.meta.env.VITE_KAKAO_REDIRECT_URL,
      code: code,
    }

    const queryString = Object.keys(data)
      .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(data[k as keyof typeof data]))
      .join('&')

    const result = await axios.post('https://kauth.kakao.com/oauth/token', queryString, {
      headers: {
        'Content-type': 'application/x-www-form-urlencoded;charset=utf-8',
      },
    })

    const signupRequest = await login({
      socialKey: result.data.access_token,
      socialType: 'KAKAO',
    })

    if (signupRequest.redirect) {
      router.push({
        path: signupRequest.redirect,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        state: { signupRequest: signupRequest as Record<string, any> },
      })
    } else {
      router.push('/')
    }
    return result
  } catch (e) {
    if (e instanceof Error) {
      alert(e.message)
      router.push('/login')
    }
    console.error(e)
    return e
  }
}

const setKakaoToken = async (code: string) => {
  const result = await getKakaoToken(code)

  if (result instanceof Error) {
    console.error(result.message)
    return
  }

  const { data } = result as AxiosResponse<KakaoTokenResponse>
  window.Kakao.Auth.setAccessToken(data.access_token)
}

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search)

  if (urlParams.has('code')) {
    const code = urlParams.get('code')
    setKakaoToken(code ?? '')
  }
})
</script>
<template>
  <div></div>
</template>
